import React, { forwardRef } from "react";
import { Stage } from "react-konva";
import Konva from "konva";
import { useTheme } from "@shared/EDS/contexts/useTheme";
import { EPDLayers } from "./EPDLayers";
import { Location } from "@opstel/query/models/location";
import { Action } from "@opstel/query/v2/models/action";
import { ShuntingUnit } from "@opstel/query/models/shuntingUnit";
import { NsSpacingDefault } from "@shared/EDS/ns-eds-types";
import { useZoomableCanvas } from "../contexts/ZoomableCanvas/useZoomableCanvas";
import { useHandleWheelToZoom } from "./hooks/useHandleWheelToZoom";

interface CanvasProps {
  readonly location: Location;
  readonly actions: Action[];
  readonly shuntingUnits: Record<string, ShuntingUnit>;
  readonly showLabels: boolean;
  readonly width: number;
  readonly height: number;
}

function CanvasWithRef(
  { location, actions, shuntingUnits, showLabels, width, height }: CanvasProps,
  ref: React.Ref<Konva.Stage>,
): React.JSX.Element {
  const { x, y, scale, dragging, setDragging } = useZoomableCanvas();
  const handleWheel = useHandleWheelToZoom();
  const dimensions = { width, height };
  const { theme } = useTheme();

  const stageStyle = {
    cursor: dragging ? "move" : "auto",
    background: theme.colors.background,
  };

  return (
    <Stage
      ref={ref}
      className="order-2"
      draggable={true}
      width={dimensions.width}
      height={dimensions.height - 40 - NsSpacingDefault.spacing4.value - 1 - 72}
      onDragEnd={() => setDragging(false)}
      onDragStart={() => setDragging(true)}
      onWheel={handleWheel}
      scaleX={scale}
      scaleY={scale}
      style={stageStyle}
      x={x}
      y={y}
    >
      <EPDLayers
        location={location}
        actions={actions}
        shuntingUnits={shuntingUnits}
        showLabels={showLabels}
      />
    </Stage>
  );
}

export const Canvas = forwardRef<Konva.Stage, CanvasProps>(CanvasWithRef);
